import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Pagination from '../components/ui/pagination';
import { Home, ChevronRight, Search, Plus, Edit2, Trash2, MapPin, Filter, Upload, Download, X, CheckCircle, AlertCircle } from 'lucide-react';

const LOCATION_TYPES = ['CITY', 'AIRPORT', 'SEAPORT', 'ICD', 'CFS', 'WAREHOUSE', 'CUSTOMS_LOCATION', 'DELIVERY_POINT'];
const MODE_OPTIONS = ['AIR', 'SEA', 'ROAD', 'RAIL', 'COURIER', 'MULTI'];

const LOCATION_CSV_TEMPLATE = `location_name,location_code,location_type,city,state,country,mode_supported,remarks
Mumbai Airport,BOM,AIRPORT,Mumbai,Maharashtra,India,AIR,
Mundra Port,MUNDRA,SEAPORT,Mundra,Gujarat,India,SEA,
Ahmedabad ICD,ICDAMD,ICD,Ahmedabad,Gujarat,India,AIR|ROAD|SEA,
Delhi Warehouse,WHDEL01,WAREHOUSE,Delhi,Delhi,India,ROAD,
`;

const TYPE_COLORS = {
  AIRPORT: 'bg-sky-100 text-sky-700',
  SEAPORT: 'bg-blue-100 text-blue-700',
  ICD: 'bg-indigo-100 text-indigo-700',
  CFS: 'bg-purple-100 text-purple-700',
  WAREHOUSE: 'bg-amber-100 text-amber-700',
  CUSTOMS_LOCATION: 'bg-rose-100 text-rose-700',
  DELIVERY_POINT: 'bg-emerald-100 text-emerald-700',
  CITY: 'bg-gray-100 text-gray-700',
};

const LocationList = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const fileInputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}` };
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterType) params.append('location_type', filterType);
      if (filterMode) params.append('mode_supported', filterMode);
      if (filterActive !== '') params.append('is_active', filterActive);
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));
      const url = `${backendUrl}/api/locations?${params.toString()}`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setLocations(data);
          setTotal(data.length);
        } else {
          setLocations(data.items || []);
          setTotal(data.total || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, getAuthHeaders, search, filterType, filterMode, filterActive, page, pageSize]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this location?')) return;
    try {
      const response = await fetch(`${backendUrl}/api/locations/${id}`, {
        method: 'DELETE', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
      });
      if (response.ok) fetchLocations();
    } catch (error) { console.error('Error:', error); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([LOCATION_CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'location_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    setCsvResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${backendUrl}/api/locations/bulk-import`, {
        method: 'POST', headers: getAuthHeaders(), body: formData
      });
      const data = await res.json();
      if (!res.ok) { setCsvResult({ error: data.detail || 'Upload failed' }); return; }
      setCsvResult(data);
      if (data.added > 0) { setPage(1); fetchLocations(); }
    } catch { setCsvResult({ error: 'Upload failed. Please try again.' }); }
    finally { setCsvUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  return (
    <div className="space-y-6" data-testid="location-list-page">
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Home className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/')}>Home</span>
        <ChevronRight className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/settings')}>Settings</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-text-dark font-medium">Location Master</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-dark font-manrope">Location Master</h1>
          <p className="text-sm text-text-secondary font-ibm mt-1">{total.toLocaleString()} locations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setShowCsvModal(true); setCsvResult(null); }} data-testid="upload-csv-location-btn" className="border-gray-200">
            <Upload className="w-4 h-4 mr-2" /> Upload CSV
          </Button>
          <Button onClick={() => navigate('/settings/locations/add')} data-testid="add-location-btn" className="bg-accent-blue hover:bg-accent-blue/90">
            <Plus className="w-4 h-4 mr-2" /> Add Location
          </Button>
        </div>
      </div>

      {/* CSV Upload Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-text-dark font-manrope">Import Locations from CSV</h2>
              <button onClick={() => setShowCsvModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-blue-50 rounded-lg p-4 text-sm font-ibm text-blue-800">
                <p className="font-semibold mb-1">Required columns:</p>
                <p className="font-mono text-xs">location_name, location_type, country</p>
                <p className="mt-1 font-semibold">Optional:</p>
                <p className="font-mono text-xs">location_code, city, state, mode_supported (AIR|ROAD|SEA), remarks</p>
                <p className="mt-1 text-xs">Valid types: {LOCATION_TYPES.join(', ')}</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate} className="w-full border-dashed border-gray-300">
                <Download className="w-4 h-4 mr-2" /> Download Template CSV
              </Button>
              <div>
                <label className="block text-sm font-medium text-text-dark font-ibm mb-2">Select CSV file</label>
                <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvUpload} disabled={csvUploading}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-accent-blue/10 file:text-accent-blue hover:file:bg-accent-blue/20 cursor-pointer" />
              </div>
              {csvUploading && <p className="text-sm text-text-secondary font-ibm text-center py-2">Uploading...</p>}
              {csvResult && !csvResult.error && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-1">
                  <div className="flex items-center gap-2 text-green-700 font-semibold font-ibm text-sm">
                    <CheckCircle className="w-4 h-4" /> Import Complete
                  </div>
                  <p className="text-sm font-ibm text-green-700">{csvResult.added} added · {csvResult.skipped} skipped (duplicates)</p>
                  {csvResult.errors?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {csvResult.errors.map((e, i) => <p key={i} className="text-xs text-red-600 font-ibm">{e}</p>)}
                    </div>
                  )}
                </div>
              )}
              {csvResult?.error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 font-ibm">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {csvResult.error}
                </div>
              )}
            </div>
            <div className="flex justify-end p-5 border-t border-gray-100">
              <Button variant="outline" onClick={() => setShowCsvModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-text-secondary" />
          <span className="text-sm font-medium text-text-dark font-ibm">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input data-testid="location-search" placeholder="Search name, code, city..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-10 h-9 text-sm border-gray-200" />
          </div>
          <Select value={filterType || "all"} onValueChange={v => { setFilterType(v === "all" ? '' : v); setPage(1); }}>
            <SelectTrigger data-testid="filter-type" className="h-9 text-sm"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {LOCATION_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterMode || "all"} onValueChange={v => { setFilterMode(v === "all" ? '' : v); setPage(1); }}>
            <SelectTrigger data-testid="filter-mode" className="h-9 text-sm"><SelectValue placeholder="All Modes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modes</SelectItem>
              {MODE_OPTIONS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterActive === '' ? "all" : filterActive} onValueChange={v => { setFilterActive(v === "all" ? '' : v); setPage(1); }}>
            <SelectTrigger data-testid="filter-active" className="h-9 text-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="location-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['Location Name', 'Code', 'Type', 'City', 'Country', 'Mode Supported', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary font-ibm uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-text-secondary font-ibm">Loading...</td></tr>
              ) : locations.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-text-secondary font-ibm">No locations found</td></tr>
              ) : (
                locations.map(loc => (
                  <tr key={loc.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`location-row-${loc.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-text-secondary shrink-0" />
                        <span className="font-medium text-sm text-text-dark font-ibm">{loc.location_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm font-mono">{loc.location_code || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[loc.location_type] || 'bg-gray-100 text-gray-700'}`}>
                        {loc.location_type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm">{loc.city || '—'}</td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm">{loc.country}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(loc.mode_supported || []).map(m => (
                          <span key={m} className="inline-flex px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-medium text-gray-600">{m}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${loc.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {loc.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`edit-location-${loc.id}`} onClick={() => navigate(`/settings/locations/edit/${loc.id}`)}>
                          <Edit2 className="w-3.5 h-3.5 text-text-secondary" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`delete-location-${loc.id}`} onClick={() => handleDelete(loc.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
        />
      </Card>
    </div>
  );
};

export default LocationList;
